import { httpsCallable } from "firebase/functions";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { functions } from "../../firebase";
import styles from "./photos.module.css";

const requestGalleryCode = httpsCallable<{ email: string; firstName: string; lastName: string }, { success: boolean }>(
  functions,
  "requestGalleryCode"
);
const verifyGalleryCode = httpsCallable<{ email: string; code: string }, { token: string; expiresAt: number }>(
  functions,
  "verifyGalleryCode"
);

const errorMessage = (error: unknown, fallback: string) => {
  const message = (error as { message?: string })?.message;
  const code = (error as { code?: string })?.code;
  return code && code !== "functions/internal" && message ? message : fallback;
};

// Remembers a sign-in that's waiting for its code, so if the tab reloads (common on phones
// when switching to the mail app) the guest lands back on "Check your email". Scoped to
// this tab via sessionStorage, kept only as long as the code is valid, and never holds
// the code itself.
const PENDING_KEY = "galleryLoginPending";
const CODE_TTL_MS = 10 * 60 * 1000; // Matches the code expiry in functions/src/gallery.ts.

interface PendingLogin {
  firstName: string;
  lastName: string;
  email: string;
  expiresAt: number;
}

const loadPendingLogin = (): PendingLogin | null => {
  try {
    const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "null") as PendingLogin | null;
    if (pending && pending.expiresAt > Date.now() && pending.email) return pending;
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // Storage unavailable or corrupt: start fresh.
  }
  return null;
};

const savePendingLogin = (pending: PendingLogin) => {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // Storage unavailable: a reload just means starting over.
  }
};

const clearPendingLogin = () => {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // Nothing to clear.
  }
};

const GalleryLogin: React.FC = () => {
  const { signInAsGuest } = useAuth();
  // Arrived from a shared photo link; it opens automatically after signing in.
  const [searchParams] = useSearchParams();
  const sharedPhoto = searchParams.has("photo");
  const [pending] = useState(loadPendingLogin);
  const [step, setStep] = useState<"details" | "code">(pending ? "code" : "details");
  const [firstName, setFirstName] = useState(pending?.firstName ?? "");
  const [lastName, setLastName] = useState(pending?.lastName ?? "");
  const [email, setEmail] = useState(pending?.email ?? "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleRequestCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      await requestGalleryCode({ email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim() });
      savePendingLogin({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        expiresAt: Date.now() + CODE_TTL_MS,
      });
      setStep("code");
    } catch (err) {
      setError(errorMessage(err, "Something went wrong. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const result = await verifyGalleryCode({ email: email.trim(), code: code.trim() });
      clearPendingLogin();
      await signInAsGuest(result.data.token);
    } catch (err) {
      setError(errorMessage(err, "That code didn't work. Please check it or request a new one."));
      setBusy(false);
    }
  };

  return (
    <div className={styles.loginCard}>
      {step === "details" ? (
        <form onSubmit={handleRequestCode}>
          <h2 className={styles.loginTitle}>View the photos</h2>
          {sharedPhoto && (
            <p className={styles.sharedHint}>Someone shared a photo with you. Sign in and it will open straight away.</p>
          )}
          <p className={styles.loginText}>
            The gallery is just for our guests. Enter your name as it appeared on your invite and your
            email, and we'll send you a code to sign in.
          </p>
          <input
            className={styles.textInput}
            placeholder="First name"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
          <input
            className={styles.textInput}
            placeholder="Last name"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
          <input
            className={styles.textInput}
            type="email"
            placeholder="Email address"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button className="primary-btn" type="submit" disabled={busy}>
            {busy ? "Sending..." : "Send me a code"}
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerifyCode}>
          <h2 className={styles.loginTitle}>Check your email</h2>
          <p className={styles.loginText}>
            If you're on our guest list, we've sent a 6-digit code to <strong>{email.trim()}</strong>.
            It expires in 10 minutes.
          </p>
          <input
            className={`${styles.textInput} ${styles.codeInput}`}
            placeholder="123456"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            required
          />
          <button className="primary-btn" type="submit" disabled={busy || code.length !== 6}>
            {busy ? "Checking..." : "View photos"}
          </button>
          <button
            className={styles.linkButton}
            type="button"
            onClick={() => {
              clearPendingLogin();
              setStep("details");
              setCode("");
              setError("");
            }}
          >
            Didn't get it? Try again
          </button>
          <p className={styles.note}>
            Can't find it? Check your spam folder. If you still have trouble, reach out to us and we'll help.
          </p>
        </form>
      )}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default GalleryLogin;
